import React, { useState } from 'react';
import { useStatsigFeatureGate } from '@americanexpress/consumer-travel-base';
import { useFeatureFlag } from '@americanexpress/one-experiments';
import { getActiveLocaleSelector, getIsLoggedInSelector, getTenancySelector } from '@americanexpress/travel-selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import PropTypes from 'prop-types';
import MosaicSearchWidgetNavigation from '../SearchWidgetNavigation/MosaicSearchWidgetNavigation';
import SearchWidgetNavigation from '../SearchWidgetNavigation/SearchWidgetNavigation';
import { DISABLE_PACKAGES_FF, VACATION_RENTALS_FF } from '../../constants/oneXpConstants';
import {
  hasCruisesRule, hasNoPackagesRule, hasVacationRentalsRule,
} from '../../constants/featureConstants';
import { useCardIsVREligible } from '../../hooks/useCardIsVREligible';
import { setSelectedTab, setXsTabOpen } from '../../duck';
import {
  CARS_TAB, CRUISES_TAB, FLIGHTS_TAB, GUESTBOOK_TAB, HOTELS_TAB, PACKAGES_TAB, VACATION_RENTALS_TAB,
} from '../../constants/linesOfBusinessConstants';
import { LinesOfBusinessContainer } from '../LineOfBusiness/LinesOfBusinessContainer';
import getTabSelected from '../../selectors/tabSelector';

const SearchWidget = ({ isMosaicDesign }) => {
  const dispatch = useDispatch();
  const tenancy = useSelector(getTenancySelector);
  const locale = useSelector(getActiveLocaleSelector);
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const selectedLineOfBusiness = useSelector(getTabSelected);

  const { properties } = useFeatureFlag(
    { featureFlagName: DISABLE_PACKAGES_FF });
  const isPackagesDisabledByRule = useConditionalFeature(hasNoPackagesRule);
  const isPackagesDisabledByFF = properties?.[tenancy]?.includes(locale);

  const { enabled: isVREligibleByFF } = useFeatureFlag(
    { featureFlagName: VACATION_RENTALS_FF });
  const isVREligibleByLocale = useConditionalFeature(hasVacationRentalsRule);
  const isCardVREligible = useCardIsVREligible();
  const isVREligibleByAccount = !isLoggedIn || isCardVREligible;
  const hasVacationRentals = isVREligibleByFF && isVREligibleByLocale && isVREligibleByAccount;

  const hasCruises = useConditionalFeature(hasCruisesRule);

  const { enabled: hasGuestbook } = useStatsigFeatureGate('guestbook_homepage_toggle');

  const enabledLOBs = [
    FLIGHTS_TAB,
    HOTELS_TAB,
    ...!hasGuestbook && hasVacationRentals ? [VACATION_RENTALS_TAB] : [],
    ...hasGuestbook ? [GUESTBOOK_TAB] : [],
    ...!(isPackagesDisabledByRule || isPackagesDisabledByFF) ? [PACKAGES_TAB] : [],
    CARS_TAB,
    ...hasCruises ? [CRUISES_TAB] : [],
  ];

  const [isOverflowing, setIsOverflowing] = useState(false);

  const onTabSelected = (selectedTab, shouldOpenXs = false) => {
    if (shouldOpenXs) {
      dispatch(setXsTabOpen(true));
    }
    if (selectedLineOfBusiness !== selectedTab) {
      dispatch(setSelectedTab(selectedTab));
    }
  };

  return isMosaicDesign ? (
    <div id="search-widget-container" className="flex flex-column">
      <MosaicSearchWidgetNavigation
        displayedLOBs={enabledLOBs}
        onTabSelected={onTabSelected}
      />
      <LinesOfBusinessContainer
        enabledLOBs={enabledLOBs}
        isMosaicDesign={true}
      />
    </div>
  ) : (
    <div id="search-widget-container" className="flex flex-column">
      <SearchWidgetNavigation
        displayedLOBs={enabledLOBs}
        isOverflowing={isOverflowing}
        setIsOverflowing={setIsOverflowing}
        onTabSelected={onTabSelected}
      />
      <div className="position-relative">
        <LinesOfBusinessContainer
          enabledLOBs={enabledLOBs}
          isOverflowing={isOverflowing}
        />
      </div>
    </div>
  );
};

SearchWidget.propTypes = {
  isMosaicDesign: PropTypes.bool,
};

export default SearchWidget;
